// Email and phone checks, matching what the API enforces.
//
// The point of validating here as well as there is the message: the API
// rejects a whole submission with one error, after the customer has filled
// in everything else. Catching it next to the field is the difference
// between a correction and a resubmission.
//
// Kept in step with _valid_email / _valid_phone in backend/app/schemas.py.
// Anything this accepts, the API must accept too, or a form will look
// happy and then fail on send.

/** Deliberately not RFC 5322: this catches what people actually mistype. */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

export const emailError = (value: string): string | undefined => {
  const email = value.trim();
  if (!email) return 'Please enter your email address.';
  if (!EMAIL_RE.test(email)) {
    return 'That email address looks incomplete — check for a typo.';
  }
  return undefined;
};

/**
 * Counts digits rather than matching a format.
 *
 * People write (555) 123-4567, 555.123.4567 and +1 555 123 4567 for the
 * same number, and rejecting any of those would be the form being wrong
 * rather than the customer. Ten digits is a US number, fifteen is the
 * international maximum.
 */
export const phoneError = (value: string, required = true): string | undefined => {
  const phone = value.trim();
  if (!phone) return required ? 'Please enter a phone number.' : undefined;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return 'That phone number looks too short.';
  if (digits.length > 15) return 'That phone number looks too long.';
  return undefined;
};

/**
 * A phone number as people write it: (555) 123-4567.
 *
 * Only reformats what it can recognise. A 10-digit number is North
 * American, and 11 digits starting with 1 is the same number with the
 * country code; anything else -- an international number, a partial one --
 * is handed back untouched rather than mangled into a shape it isn't.
 * Formatting is for display and for tidying a field on blur, never while
 * someone is still typing.
 */
export const formatPhone = (value?: string): string => {
  const raw = (value ?? '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length !== 10) return raw;
  const formatted = `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  return digits.length === 11 ? `+1 ${formatted}` : formatted;
};

/** A number as a dialler wants it: digits and a leading + only. */
const dialable = (value?: string): string => (value ?? '').replace(/[^\d+]/g, '');

/** What a phone number should be dialled as. */
export const phoneHref = (value?: string): string => `tel:${dialable(value)}`;

/**
 * What a phone number should be texted as.
 *
 * Opens Messages on an iPhone and the default SMS app elsewhere. Plain
 * `sms:` with no body, so it lands in the thread with the cursor ready
 * rather than putting words in the shop's mouth -- the invoice link has
 * its own share button for when there is something specific to send.
 */
export const smsHref = (value?: string): string => `sms:${dialable(value)}`;

/**
 * Tidy an address without changing which mailbox it means.
 *
 * The domain is case-insensitive so lowercasing it is safe and makes a
 * list scan evenly; the local part is left alone, because a handful of
 * mail servers do treat it as case-sensitive and silently rewriting
 * someone's address is not worth the tidier column.
 */
export const normalizeEmail = (value?: string): string => {
  const email = (value ?? '').trim();
  const at = email.lastIndexOf('@');
  if (at < 1) return email;
  return `${email.slice(0, at)}@${email.slice(at + 1).toLowerCase()}`;
};
